(() => {
  if (window.FBCardActions) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const STORE_KEY = 'fb-compare';
  const MAX_COMPARE = 4;
  const productCache = new Map();

  /* ---------- helpers ---------- */
  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (cents) => {
    const format = window.FBMoneyFormat;
    if (format) {
      const fmt = (n, d, t = ',', s = '.') => {
        const [i, f] = (n / 100).toFixed(d).split('.');
        return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? s + f : '');
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
        amount: fmt(cents, 2),
        amount_no_decimals: fmt(cents, 0),
        amount_with_comma_separator: fmt(cents, 2, '.', ','),
        amount_no_decimals_with_comma_separator: fmt(cents, 0, '.'),
        amount_with_apostrophe_separator: fmt(cents, 2, "'"),
        amount_no_decimals_with_space_separator: fmt(cents, 0, ' '),
        amount_with_space_separator: fmt(cents, 2, ' ', ',')
      }[key] ?? fmt(cents, 2))).replace(/<[^>]*>/g, '');
    }
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);
    } catch (_) {
      return `₹${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;
    }
  };
  const img = (src, width) => {
    if (!src) return '';
    const url = typeof src === 'string' ? src : src.src;
    if (!url) return '';
    try {
      const u = new URL(url.startsWith('//') ? `https:${url}` : url, location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (_) { return url; }
  };
  const stripHtml = (html, length) => {
    const area = document.createElement('textarea');
    area.innerHTML = String(html || '').replace(/<(style|script)[\s\S]*?<\/\1>/gi, ' ').replace(/<[^>]+>/g, ' ');
    const text = area.value.replace(/\s+/g, ' ').trim();
    return length && text.length > length ? `${text.slice(0, length).trim()}…` : text;
  };
  const icon = {
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg>',
    chevron: '<svg viewBox="0 0 12 8" aria-hidden="true"><path d="m1 1 5 5 5-5"/></svg>'
  };

  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
        .then((res) => { if (!res.ok) throw new Error('Product not found'); return res.json(); })
        .catch((err) => { productCache.delete(handle); throw err; }));
    }
    return productCache.get(handle);
  };

  /* ---------- toast ---------- */
  let toast; let toastTimer;
  const showToast = ({ title, text, image, error }) => {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fb-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.classList.toggle('is-error', !!error);
    toast.innerHTML = `${image ? `<img src="${esc(img(image, 120))}" alt="">` : ''}<div><b>${esc(title)}</b>${esc(text || '')}</div>${error ? '' : `<a href="${ROOT}cart">View bag</a>`}`;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3800);
  };

  /* ---------- cart ---------- */
  const updateCartCount = async () => {
    try {
      const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const count = cart.item_count;
      let bubbles = document.querySelectorAll('.fb-cart-count');
      if (!bubbles.length) {
        document.querySelectorAll('.fb-icons a[href$="/cart"]').forEach((link) => {
          const span = document.createElement('span');
          span.className = 'fb-cart-count';
          link.appendChild(span);
        });
        bubbles = document.querySelectorAll('.fb-cart-count');
      }
      bubbles.forEach((el) => { el.textContent = count; el.hidden = count === 0; });
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => { el.textContent = count; });
      document.dispatchEvent(new CustomEvent('fb:cart:updated', { detail: cart }));
    } catch (_) { /* count refresh is best-effort */ }
  };

  const addToCart = async (variantId, quantity = 1, formData = null) => {
    const res = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: formData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: formData || JSON.stringify({ items: [{ id: Number(variantId), quantity }] })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag');
    await updateCartCount();
    if (location.pathname.replace(/\/$/, '') === `${ROOT}cart`.replace(/\/$/, '') && window.cartBase) {
      await window.cartBase.refreshCart();
    }
    return data.items ? data.items[0] : data;
  };

  // Custom premium forms are outside the original theme's product-form element.
  // Handle their adds once, keep the customer on the page and show feedback.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches('.fb-card__quick, .fb-pdp-form')) return;
    event.preventDefault();
    if (form.dataset.fbAdding === 'true') return;
    const button = event.submitter || form.querySelector('[name="add"]');
    const label = button?.innerHTML;
    form.dataset.fbAdding = 'true';
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); button.textContent = 'Adding…'; }
    try {
      const item = await addToCart(null, 1, new FormData(form));
      showToast({ title: 'Added to bag', text: item.product_title || item.title, image: item.image });
    } catch (error) {
      showToast({ title: 'Could not add to bag', text: error.message, error: true });
    } finally {
      delete form.dataset.fbAdding;
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.innerHTML = label; }
    }
  });

  /* ---------- modal shell ---------- */
  const modals = {};
  let lastFocus = null;
  const getModal = (name) => {
    if (modals[name]) return modals[name];
    const el = document.createElement('div');
    el.className = `fb-modal fb-modal--${name}`;
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `<div class="fb-modal__overlay" data-fb-modal-close></div>
      <div class="fb-modal__dialog" role="dialog" aria-modal="true" aria-label="${({ compare: 'Compare products', drawer: 'Choose options' })[name] || 'Quick view'}" tabindex="-1">
        <button class="fb-modal__close" type="button" aria-label="Close" data-fb-modal-close>${icon.close}</button>
        <div class="fb-modal__body"></div>
      </div>`;
    el.addEventListener('click', (e) => { if (e.target.closest('[data-fb-modal-close]')) closeModal(name); });
    document.body.appendChild(el);
    modals[name] = el;
    return el;
  };
  const openModal = (name, html) => {
    const el = getModal(name);
    Object.keys(modals).forEach((key) => { if (key !== name) closeModal(key, true); });
    el.querySelector('.fb-modal__body').innerHTML = html;
    if (!el.classList.contains('is-open')) {
      lastFocus = document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('fb-modal-open');
      setTimeout(() => el.querySelector('.fb-modal__close').focus(), 30);
    }
    return el;
  };
  const closeModal = (name, silent) => {
    const el = modals[name];
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('fb-modal-open');
    if (!silent && lastFocus && lastFocus.focus) lastFocus.focus();
  };
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') Object.keys(modals).forEach((key) => closeModal(key));
  });
  const loading = '<div class="fb-modal__loading"><div class="fb-spinner" role="status" aria-label="Loading"></div></div>';

  /* ---------- colors ---------- */
  const colorMap = {};
  String(window.FBColorMap || '').split(';').forEach((pair) => {
    const i = pair.indexOf(':');
    if (i > 0) colorMap[pair.slice(0, i).replace(/-/g, '')] = pair.slice(i + 1);
  });
  const colorFor = (name) => {
    const words = String(name || '').toLowerCase().replace(/&/g, ' ').split(/[\s_-]+/).filter(Boolean);
    const key = words.join('');
    if (colorMap[key]) return colorMap[key];
    const last = words[words.length - 1];
    if (last && colorMap[last]) return colorMap[last];
    if (words[0] && colorMap[words[0]]) return colorMap[words[0]];
    for (const w of [words[0], last]) if (w && window.CSS && CSS.supports('color', w)) return w;
    return '#d9c7c2';
  };
  const isColorOption = (name) => /colou?r|shade/i.test(name);
  const isSizeOption = (name) => /size/i.test(name);

  /* ---------- quick view / choose options ---------- */
  const OFF = (v) => (v.compare_at_price > v.price ? Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price) : 0);
  const priceHtml = (variant) => {
    const off = OFF(variant);
    return `<strong>${money(variant.price)}</strong>${off ? `<s>${money(variant.compare_at_price)}</s><span class="fb-qv__off">${off}% OFF</span>` : ''}`;
  };
  const trustHtml = `<ul class="fb-qv__trust">
      <li><svg viewBox="0 0 24 24"><path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg>Secure payments</li>
      <li><svg viewBox="0 0 24 24"><path d="M4 12a8 8 0 1 0 2.4-5.7"/><path d="M4 4v4h4"/></svg>7-day easy returns</li>
      <li><svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/></svg>Cash on delivery</li>
    </ul>`;

  const openQuickView = async (handle, mode = 'quickview') => {
    const modalName = mode === 'drawer' ? 'drawer' : 'quickview';
    const modal = openModal(modalName, loading);
    let product;
    try { product = await fetchProduct(handle); }
    catch (_) {
      modal.querySelector('.fb-modal__body').innerHTML = '<div class="fb-cmp__empty"><p>Sorry, this product could not be loaded.</p></div>';
      return;
    }
    const variants = product.variants;
    let current = variants.find((v) => v.available) || variants[0];
    const media = (product.media && product.media.length
      ? product.media.filter((m) => m.media_type === 'image').map((m) => ({ id: m.id, src: m.src }))
      : (product.images || []).map((src) => ({ id: null, src })));
    const singleVariant = variants.length === 1 && /default title/i.test(variants[0].title);
    const optionName = (opt) => (typeof opt === 'string' ? opt : opt.name);
    const optionValues = (opt, i) => (typeof opt === 'string' ? [...new Set(variants.map((v) => v.options[i]))] : opt.values);

    const optionsHtml = singleVariant ? '' : product.options.map((opt, i) => {
      const name = optionName(opt);
      const values = optionValues(opt, i);
      if (isColorOption(name)) {
        return `<fieldset class="fb-qv__option" data-index="${i}">
          <legend>${esc(name)}: <b data-fb-qv-selected>${esc(current.options[i])}</b></legend>
          <div class="fb-qv__swatches">${values.map((val) => `<button type="button" class="fb-qv__swatch" data-value="${esc(val)}" title="${esc(val)}" aria-label="${esc(val)}" style="--swatch:${esc(colorFor(val))}"></button>`).join('')}</div>
        </fieldset>`;
      }
      return `<fieldset class="fb-qv__option" data-index="${i}">
        <legend>${esc(name)}: <b data-fb-qv-selected>${esc(current.options[i])}</b>${isSizeOption(name) ? '<a class="fb-qv__guide" href="/pages/size-chart">Size guide</a>' : ''}</legend>
        <div class="fb-qv__chips">${values.map((val) => `<button type="button" class="fb-qv__chip" data-value="${esc(val)}">${esc(val)}</button>`).join('')}</div>
      </fieldset>`;
    }).join('');

    const buyHtml = `<div class="fb-qv__buy">
        <div class="fb-qty"><button type="button" data-qty="-1" aria-label="Decrease quantity">−</button><input type="number" min="1" value="1" aria-label="Quantity" data-fb-qv-qty><button type="button" data-qty="1" aria-label="Increase quantity">+</button></div>
        <button class="fb-qv__add" type="button" data-fb-qv-add><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 8h14l-1 13H6L5 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg><span>Add to bag</span></button>
      </div>
      <button class="fb-qv__now" type="button" data-fb-qv-now>Buy it now</button>
      <p class="fb-qv__msg" data-fb-qv-msg role="status"></p>`;

    const body = modal.querySelector('.fb-modal__body');
    if (modalName === 'drawer') {
      body.innerHTML = `<div class="fb-qd">
        <div class="fb-qd__head"><h2>Choose options</h2></div>
        <div class="fb-qd__scroll">
          <div class="fb-qd__product">
            <a class="fb-qd__thumb" href="${esc(product.url)}"><img data-fb-qv-main src="${esc(img(media[0] && media[0].src, 300))}" alt="${esc(product.title)}"></a>
            <div>
              <a class="fb-qd__title" href="${esc(product.url)}">${esc(product.title)}</a>
              <div class="fb-qv__rating"><span>★★★★★</span> 4.8</div>
              <div class="fb-qv__price fb-qv__price--sm" data-fb-qv-price></div>
            </div>
          </div>
          ${optionsHtml}
        </div>
        <div class="fb-qd__foot">${buyHtml}<a class="fb-qv__details" href="${esc(product.url)}">View full details</a></div>
      </div>`;
    } else {
      body.innerHTML = `<div class="fb-qv">
        <div class="fb-qv__media">
          ${media.length > 1 ? `<div class="fb-qv__thumbs">${media.slice(0, 12).map((m, i) => `<button type="button" data-i="${i}" aria-label="Show image ${i + 1}"><img src="${esc(img(m.src, 160))}" alt=""></button>`).join('')}</div>` : ''}
          <div class="fb-qv__stage">
            <div class="fb-qv__track" data-fb-qv-track>${media.map((m, i) => `<figure><img src="${esc(img(m.src, 1000))}" alt="${esc(product.title)}"></figure>`).join('') || '<figure></figure>'}</div>
            ${media.length > 1 ? `<button class="fb-qv__arrow fb-qv__arrow--prev" type="button" data-fb-qv-step="-1" aria-label="Previous image"><svg viewBox="0 0 24 24"><path d="m15 5-7 7 7 7"/></svg></button><button class="fb-qv__arrow fb-qv__arrow--next" type="button" data-fb-qv-step="1" aria-label="Next image"><svg viewBox="0 0 24 24"><path d="m9 5 7 7-7 7"/></svg></button><div class="fb-qv__count"><span data-fb-qv-index>1</span> / ${media.length}</div>` : ''}
            ${OFF(current) ? `<span class="fb-qv__badge">Sale</span>` : ''}
          </div>
        </div>
        <div class="fb-qv__info">
          ${product.vendor ? `<div class="fb-qv__vendor">${esc(product.vendor)}</div>` : ''}
          <h2 class="fb-qv__title"><a href="${esc(product.url)}">${esc(product.title)}</a></h2>
          <div class="fb-qv__rating"><span>★★★★★</span> 4.8 <i></i> ${product.type ? esc(product.type) : 'Bestseller'}</div>
          <div class="fb-qv__price" data-fb-qv-price></div>
          <p class="fb-qv__tax">Inclusive of all taxes</p>
          ${product.description ? `<p class="fb-qv__desc">${esc(stripHtml(product.description, 170))}</p>` : ''}
          ${optionsHtml}
          ${buyHtml}
          ${trustHtml}
          <a class="fb-qv__details" href="${esc(product.url)}">View full details <span aria-hidden="true">→</span></a>
        </div>
      </div>`;
    }

    const addBtn = body.querySelector('[data-fb-qv-add]');
    const nowBtn = body.querySelector('[data-fb-qv-now]');
    const msg = body.querySelector('[data-fb-qv-msg]');
    const track = body.querySelector('[data-fb-qv-track]');
    const drawerImg = body.querySelector('.fb-qd [data-fb-qv-main]');
    let index = 0;

    let scrollLock = 0;
    const markActive = (i) => {
      index = i;
      body.querySelectorAll('.fb-qv__thumbs button').forEach((b) => b.classList.toggle('is-active', Number(b.dataset.i) === index));
      const counter = body.querySelector('[data-fb-qv-index]');
      if (counter) counter.textContent = index + 1;
      const thumb = body.querySelector('.fb-qv__thumbs button.is-active');
      if (thumb) thumb.scrollIntoView({ block: 'nearest' });
    };
    const goTo = (i, smooth = true) => {
      if (!media.length) return;
      index = (i + media.length) % media.length;
      if (track) {
        scrollLock = Date.now() + 300;
        track.scrollLeft = track.clientWidth * index;
        if (smooth) { track.classList.remove('is-fading'); void track.offsetWidth; track.classList.add('is-fading'); }
      }
      if (drawerImg) drawerImg.src = img(media[index].src, 300);
      markActive(index);
    };
    const showVariantMedia = (variant) => {
      const fm = variant.featured_media || variant.featured_image;
      if (!fm) return;
      const i = media.findIndex((m) => (fm.id && m.id === fm.id) || img(m.src, 1) === img(fm.src || fm.preview_image?.src, 1));
      if (i > -1) goTo(i);
    };
    const setLabel = (text) => { addBtn.querySelector('span').textContent = text; };
    const refresh = () => {
      body.querySelector('[data-fb-qv-price]').innerHTML = priceHtml(current);
      addBtn.disabled = !current.available;
      if (nowBtn) nowBtn.disabled = !current.available;
      setLabel(current.available ? 'Add to bag' : 'Sold out');
      body.querySelectorAll('.fb-qv__option').forEach((fs) => {
        const i = Number(fs.dataset.index);
        fs.querySelector('[data-fb-qv-selected]').textContent = current.options[i];
        fs.querySelectorAll('[data-value]').forEach((b) => {
          const active = b.dataset.value === current.options[i];
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', String(active));
          const match = variants.find((v) => v.options.every((o, j) => (j === i ? o === b.dataset.value : o === current.options[j])));
          b.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    };
    refresh();
    showVariantMedia(current);

    if (track && 'ResizeObserver' in window) {
      new ResizeObserver(() => {
        if (!track.clientWidth) return;
        scrollLock = Date.now() + 300;
        track.scrollLeft = track.clientWidth * index;
      }).observe(track);
    }
    if (track) {
      let scrollTimer;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (Date.now() < scrollLock) return;
          const i = Math.round(track.scrollLeft / track.clientWidth);
          if (i !== index) markActive(i);
        }, 80);
      }, { passive: true });
    }

    const submit = async (checkout) => {
      if (!current.available) return;
      const qty = Math.max(1, parseInt(body.querySelector('[data-fb-qv-qty]').value, 10) || 1);
      const btn = checkout ? nowBtn : addBtn;
      btn.disabled = true;
      btn.classList.add('is-loading');
      if (!checkout) setLabel('Adding…');
      try {
        await addToCart(current.id, qty);
        if (checkout) { window.location.href = `${ROOT}checkout`; return; }
        closeModal(modalName);
        showToast({ title: 'Added to bag', text: `${product.title}${singleVariant ? '' : ` – ${current.title}`}`, image: (current.featured_image && current.featured_image.src) || (media[0] && media[0].src) });
      } catch (err) {
        msg.textContent = err.message;
      } finally {
        btn.disabled = !current.available;
        btn.classList.remove('is-loading');
        setLabel(current.available ? 'Add to bag' : 'Sold out');
      }
    };

    body.onclick = (e) => {
      const thumb = e.target.closest('.fb-qv__thumbs button');
      if (thumb) { goTo(Number(thumb.dataset.i)); return; }
      const step = e.target.closest('[data-fb-qv-step]');
      if (step) { goTo(index + Number(step.dataset.fbQvStep)); return; }
      const valueBtn = e.target.closest('.fb-qv__option [data-value]');
      if (valueBtn) {
        const i = Number(valueBtn.closest('.fb-qv__option').dataset.index);
        const wanted = current.options.slice();
        wanted[i] = valueBtn.dataset.value;
        current = variants.find((v) => v.options.every((o, j) => o === wanted[j]))
          || variants.find((v) => v.options[i] === wanted[i] && v.available)
          || variants.find((v) => v.options[i] === wanted[i])
          || current;
        msg.textContent = '';
        refresh();
        if (isColorOption(optionName(product.options[i])) || i === 0) showVariantMedia(current);
        return;
      }
      const qtyBtn = e.target.closest('[data-qty]');
      if (qtyBtn) {
        const input = body.querySelector('[data-fb-qv-qty]');
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(qtyBtn.dataset.qty));
        return;
      }
      if (e.target.closest('[data-fb-qv-add]')) submit(false);
      if (e.target.closest('[data-fb-qv-now]')) submit(true);
    };
  };

  /* ---------- card swatches: preview the color's photo ---------- */
  const swapCardImage = (swatch) => {
    const card = swatch.closest('.fb-card');
    const image = card && card.querySelector('.fb-card__media img');
    if (!image || !swatch.dataset.fbSwatchImg) return;
    if (!image.dataset.fbOriginal) image.dataset.fbOriginal = image.currentSrc || image.src;
    image.removeAttribute('srcset');
    image.src = swatch.dataset.fbSwatchImg;
    card.querySelectorAll('.fb-card__swatch').forEach((s) => s.classList.toggle('is-active', s === swatch));
  };
  document.addEventListener('mouseover', (e) => {
    const swatch = e.target.closest && e.target.closest('.fb-card__swatch');
    if (swatch) swapCardImage(swatch);
  });

  /* ---------- compare ---------- */
  const readCompare = () => {
    try { const list = JSON.parse(localStorage.getItem(STORE_KEY) || '[]'); return Array.isArray(list) ? list.slice(0, MAX_COMPARE) : []; }
    catch (_) { return []; }
  };
  const writeCompare = (list) => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(list)); } catch (_) { /* storage blocked */ }
    compareList = list;
    renderCompareUi();
  };
  let compareList = readCompare();
  let barCollapsed = false;

  const toggleCompare = async (handle, button) => {
    if (compareList.some((p) => p.handle === handle)) {
      writeCompare(compareList.filter((p) => p.handle !== handle));
      return;
    }
    if (compareList.length >= MAX_COMPARE) {
      showToast({ title: 'Compare is full', text: `You can compare up to ${MAX_COMPARE} products. Remove one to add another.`, error: true });
      return;
    }
    button && button.classList.add('is-loading');
    try {
      const p = await fetchProduct(handle);
      writeCompare([...compareList, { handle, title: p.title, url: p.url, image: p.featured_image || (p.images && p.images[0]) || '' }]);
      barCollapsed = false;
      renderCompareUi();
    } catch (_) {
      showToast({ title: 'Something went wrong', text: 'Could not add this product to compare.', error: true });
    } finally {
      button && button.classList.remove('is-loading');
    }
  };

  let bar;
  const ensureBar = () => {
    if (bar) return bar;
    bar = document.createElement('section');
    bar.className = 'fb-cbar';
    bar.setAttribute('aria-label', 'Compare products');
    bar.innerHTML = `<button class="fb-cbar__toggle" type="button" data-fb-cbar-toggle aria-expanded="true"><span>Compare (<span data-fb-cbar-count>0</span>)</span>${icon.chevron}</button>
      <div class="fb-cbar__inner">
        <div class="fb-cbar__title">Compare products<small>Add up to ${MAX_COMPARE} products</small></div>
        <div class="fb-cbar__slots" data-fb-cbar-slots></div>
        <div class="fb-cbar__actions">
          <button class="fb-btn" type="button" data-fb-compare-open>Compare now</button>
          <button class="fb-link-btn" type="button" data-fb-compare-clear>Clear all</button>
        </div>
      </div>`;
    bar.addEventListener('click', (e) => {
      if (e.target.closest('[data-fb-cbar-toggle]')) { barCollapsed = !barCollapsed; renderCompareUi(); return; }
      const remove = e.target.closest('[data-fb-compare-remove]');
      if (remove) writeCompare(compareList.filter((p) => p.handle !== remove.dataset.fbCompareRemove));
    });
    document.body.appendChild(bar);
    return bar;
  };

  const renderCompareUi = () => {
    const handles = compareList.map((p) => p.handle);
    document.querySelectorAll('.fb-ca[data-fb-ca-handle]').forEach((wrap) => {
      const btn = wrap.querySelector('[data-fb-ca="compare"]');
      if (!btn) return;
      const active = handles.includes(wrap.dataset.fbCaHandle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
    document.querySelectorAll('[data-fb-compare-count]').forEach((el) => {
      el.textContent = compareList.length;
      el.hidden = compareList.length === 0;
    });

    const b = ensureBar();
    b.querySelector('[data-fb-cbar-count]').textContent = compareList.length;
    const slots = [];
    for (let i = 0; i < MAX_COMPARE; i += 1) {
      const p = compareList[i];
      slots.push(p
        ? `<div class="fb-cbar__slot is-filled">${p.image ? `<img src="${esc(img(p.image, 140))}" alt="">` : ''}<a href="${esc(p.url)}">${esc(p.title)}</a><button class="fb-cbar__remove" type="button" data-fb-compare-remove="${esc(p.handle)}" aria-label="Remove ${esc(p.title)} from compare">×</button></div>`
        : `<div class="fb-cbar__slot is-empty"><span>Add product</span></div>`);
    }
    b.querySelector('[data-fb-cbar-slots]').innerHTML = slots.join('');
    b.querySelector('[data-fb-compare-open]').disabled = compareList.length < 2;
    b.classList.toggle('is-visible', compareList.length > 0);
    b.classList.toggle('is-collapsed', barCollapsed);
    b.querySelector('[data-fb-cbar-toggle]').setAttribute('aria-expanded', String(!barCollapsed));
    document.body.style.paddingBottom = compareList.length && !barCollapsed ? `${b.offsetHeight}px` : '';

    if (modals.compare && modals.compare.classList.contains('is-open')) renderCompareTable();
  };

  const renderCompareTable = async () => {
    const modal = openModal('compare', loading);
    const body = modal.querySelector('.fb-modal__body');
    if (!compareList.length) {
      body.innerHTML = `<div class="fb-cmp"><div class="fb-cmp__head"><h2>Compare products</h2></div><div class="fb-cmp__empty"><p>You haven't added any products to compare yet. Use the compare icon on any product to add it here.</p><button class="fb-btn" type="button" data-fb-modal-close>Continue shopping</button></div></div>`;
      return;
    }
    const results = await Promise.allSettled(compareList.map((p) => fetchProduct(p.handle)));
    const products = results.map((r, i) => (r.status === 'fulfilled' ? r.value : null)).filter(Boolean);
    const optionNames = [];
    products.forEach((p) => p.options.forEach((o) => {
      const name = typeof o === 'string' ? o : o.name;
      if (!/^title$/i.test(name) && !optionNames.includes(name)) optionNames.push(name);
    }));
    const optionValues = (p, name) => {
      const i = p.options.findIndex((o) => (typeof o === 'string' ? o : o.name) === name);
      if (i < 0) return '—';
      const o = p.options[i];
      const values = typeof o === 'string' ? [...new Set(p.variants.map((v) => v.options[i]))] : o.values;
      if (isColorOption(name)) return `<span class="fb-cmp__colors">${values.map((v) => `<span title="${esc(v)}"><i style="--swatch:${esc(colorFor(v))}"></i>${esc(v)}</span>`).join('')}</span>`;
      return esc(values.join(', '));
    };
    const cell = (fn) => products.map((p) => `<td>${fn(p)}</td>`).join('');
    const rows = [
      ['Product', (p) => `<div class="fb-cmp__product"><a href="${esc(p.url)}">${p.featured_image ? `<img src="${esc(img(p.featured_image, 500))}" alt="${esc(p.title)}" loading="lazy">` : ''}</a><a href="${esc(p.url)}">${esc(p.title)}</a><button class="fb-cbar__remove" type="button" data-fb-compare-remove="${esc(p.handle)}" aria-label="Remove ${esc(p.title)} from compare">×</button></div>`],
      ['Price', (p) => `<div class="fb-cmp__price"><strong>${p.price_varies ? 'From ' : ''}${money(p.price)}</strong>${p.compare_at_price && p.compare_at_price > p.price ? `<s>${money(p.compare_at_price)}</s>` : ''}</div>`],
      ['Availability', (p) => (p.available ? '<span class="fb-cmp__stock--in">In stock</span>' : '<span class="fb-cmp__stock--out">Sold out</span>')],
      ...(products.some((p) => p.type) ? [['Category', (p) => esc(p.type || '—')]] : []),
      ...(products.some((p) => p.vendor) ? [['Brand', (p) => esc(p.vendor || '—')]] : []),
      ...optionNames.map((name) => [name, (p) => optionValues(p, name)]),
      ['Description', (p) => esc(stripHtml(p.description, 180)) || '—'],
      ['', (p) => (p.available
        ? `<button class="fb-btn" type="button" data-fb-cmp-add="${esc(p.handle)}">${p.variants.length > 1 ? 'Choose options' : 'Add to bag'}</button>`
        : `<a class="fb-btn fb-btn--ghost" href="${esc(p.url)}">View product</a>`)]
    ];
    body.innerHTML = `<div class="fb-cmp">
      <div class="fb-cmp__head"><h2>Compare products</h2><button class="fb-link-btn" type="button" data-fb-compare-clear>Clear all</button></div>
      <div class="fb-cmp__scroll"><table><tbody>${rows.map(([label, fn]) => `<tr><th scope="row">${esc(label)}</th>${cell(fn)}</tr>`).join('')}</tbody></table></div>
    </div>`;
  };

  /* ---------- event delegation ---------- */
  document.addEventListener('click', async (e) => {
    const action = e.target.closest('[data-fb-ca]');
    if (action) {
      e.preventDefault();
      e.stopPropagation();
      const handle = action.closest('[data-fb-ca-handle]').dataset.fbCaHandle;
      const type = action.dataset.fbCa;
      if (type === 'quickview') openQuickView(handle);
      if (type === 'compare') toggleCompare(handle, action);
      if (type === 'cart' && !action.disabled) {
        if (action.dataset.single !== 'true') { openQuickView(handle, 'drawer'); return; }
        action.classList.add('is-loading');
        try {
          await addToCart(action.dataset.variantId, 1);
          const p = await fetchProduct(handle).catch(() => null);
          showToast({ title: 'Added to bag', text: p ? p.title : '', image: p && p.featured_image });
        } catch (err) {
          showToast({ title: 'Could not add to bag', text: err.message, error: true });
        } finally {
          action.classList.remove('is-loading');
        }
      }
      return;
    }
    const cardSwatch = e.target.closest('.fb-card__swatch');
    if (cardSwatch) { e.preventDefault(); swapCardImage(cardSwatch); return; }
    if (e.target.closest('[data-fb-compare-open]')) { e.preventDefault(); renderCompareTable(); return; }
    if (e.target.closest('[data-fb-compare-clear]')) { writeCompare([]); return; }
    const remove = e.target.closest('.fb-modal--compare [data-fb-compare-remove]');
    if (remove) { writeCompare(compareList.filter((p) => p.handle !== remove.dataset.fbCompareRemove)); return; }
    const cmpAdd = e.target.closest('[data-fb-cmp-add]');
    if (cmpAdd) {
      const handle = cmpAdd.dataset.fbCmpAdd;
      const p = await fetchProduct(handle);
      if (p.variants.length > 1) { openQuickView(handle, 'drawer'); return; }
      cmpAdd.disabled = true;
      try {
        await addToCart(p.variants[0].id, 1);
        showToast({ title: 'Added to bag', text: p.title, image: p.featured_image });
      } catch (err) {
        showToast({ title: 'Could not add to bag', text: err.message, error: true });
      } finally { cmpAdd.disabled = false; }
    }
  }, true);

  window.addEventListener('storage', (e) => { if (e.key === STORE_KEY) { compareList = readCompare(); renderCompareUi(); } });
  window.addEventListener('resize', () => { if (bar && compareList.length && !barCollapsed) document.body.style.paddingBottom = `${bar.offsetHeight}px`; });

  // Keep compare states in sync for cards added later (sliders, filtering, recommendations).
  let syncQueued = false;
  new MutationObserver((mutations) => {
    if (syncQueued) return;
    if (!mutations.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches('.fb-ca') || n.querySelector('.fb-ca'))))) return;
    syncQueued = true;
    requestAnimationFrame(() => { syncQueued = false; renderCompareUi(); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  const init = () => renderCompareUi();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.FBCardActions = { colorFor, openQuickView, toggleCompare, openCompare: renderCompareTable, addToCart };
})();


/* Scroll-triggered entrance; first-screen and shopping controls remain visible. */
(() => {
  if (window.FBSiteMotion || !('IntersectionObserver' in window)) return;
  window.FBSiteMotion = true;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selector = 'main h1:not(.fb-home-banners__sr),main h2,.fb-card,.fb-category,.fb-need-card,.fb-collection-card,.fb-collection-circle,.fb-promise,.fb-collection-banner,.fb-pdp-gallery,.fb-review-card,main .rte';
  const seen = new WeakSet();
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      entry.target.classList.remove('fb-motion-pending');
      if (!preference.matches) entry.target.classList.add('fb-motion-revealed');
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -48px 0px' });
  const scan = (root = document) => {
    root.querySelectorAll(selector).forEach(element => {
      if (seen.has(element) || element.closest('.fb-cart,.fb-modal,[role="dialog"]') || element.parentElement.closest('.fb-card,.fb-need-card,.fb-collection-card,.fb-promise,.fb-collection-banner')) return;
      seen.add(element);
      if (preference.matches) return;
      const siblings = Array.from(element.parentElement.children);
      element.style.setProperty('--fb-motion-delay', `${Math.min(siblings.indexOf(element) % 4, 3) * 55}ms`);
      if (element.getBoundingClientRect().top >= window.innerHeight) element.classList.add('fb-motion-pending');
      observer.observe(element);
    });
  };
  const start = () => {
    scan();
    // Fail open if a browser stops delivering observer callbacks.
    window.setTimeout(() => document.querySelectorAll('.fb-motion-pending').forEach(element => element.classList.remove('fb-motion-pending')), 45000);
    let scheduled = false;
    new MutationObserver(records => {
      if (scheduled || !records.some(record => record.addedNodes.length)) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; scan(); });
    }).observe(document.querySelector('main') || document.body, {childList:true,subtree:true});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
  preference.addEventListener('change', () => {
    if (preference.matches) {
      observer.disconnect();
      document.querySelectorAll('.fb-motion-revealed,.fb-motion-pending').forEach(element => element.classList.remove('fb-motion-revealed','fb-motion-pending'));
    }
  });
})();
